import { createFileRoute } from "@tanstack/react-router";
import { LegalPage, P, UL, H3 } from "@/components/marketing/LegalPage";
import { alternateLinkList } from "@/lib/i18n/locale";

export const Route = createFileRoute("/vi/terms")({
  head: () => ({
    meta: [
      { title: "Điều khoản Dịch vụ — banana.cards" },
      { name: "description", content: "Các điều khoản quản lý việc bạn sử dụng các sản phẩm và dịch vụ số và vật lý của Banana Cards." },
      { property: "og:title", content: "Điều khoản Dịch vụ — banana.cards" },
      { property: "og:description", content: "Đọc Điều khoản Dịch vụ của Banana Cards." },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/terms", "vi"),
  }),
  component: TermsPageVi,
});

function TermsPageVi() {
  return (
    <LegalPage
      eyebrow="Pháp lý"
      title="Điều khoản"
      italic="Dịch vụ."
      updated="Tháng 1, 2026"
      intro="Chào mừng đến với Banana Cards. Các Điều khoản này điều chỉnh việc bạn truy cập và sử dụng các sản phẩm và dịch vụ số và vật lý của chúng tôi — bao gồm website, ứng dụng, thẻ NFC, hồ sơ số, CRM, công cụ AI, và nhiều hơn nữa. Khi sử dụng bất kỳ phần nào của Dịch vụ, bạn đồng ý bị ràng buộc bởi các Điều khoản này."
      related={[
        { label: "Chính sách Bảo mật", to: "/vi/privacy" },
        { label: "Hướng dẫn Pháp lý", to: "/vi/legal-guidelines" },
        { label: "Liên hệ với chúng tôi", to: "/vi/contact" },
      ]}
      sections={[
        {
          id: "general",
          title: "Điều khoản Chung & Phạm vi Áp dụng",
          body: (
            <>
              <P>Các Điều khoản này áp dụng cho tất cả người dùng, khách hàng, đối tác và cộng tác viên tương tác với Banana Cards Company Limited và Six Studio Creative FZCO ("Banana Cards", "chúng tôi").</P>
              <P>Chúng tôi có quyền cập nhật hoặc sửa đổi các Điều khoản này vào bất kỳ lúc nào. Điều khoản được cập nhật có hiệu lực khi được công bố. Việc tiếp tục sử dụng Dịch vụ sau khi các thay đổi có hiệu lực đồng nghĩa với việc chấp nhận Điều khoản đã sửa đổi.</P>
              <P>Nếu bạn không đồng ý với các Điều khoản này, bạn không được sử dụng Dịch vụ.</P>
            </>
          ),
        },
        {
          id: "services",
          title: "Mô tả Dịch vụ",
          body: (
            <>
              <P>Banana Cards cung cấp một nền tảng nhận diện số và kết nối bao gồm:</P>
              <UL items={[
                "Danh thiếp NFC vật lý",
                "Thẻ ảo (chỉ dạng số)",
                "Hồ sơ số và mini-site được lưu trữ",
                "Quản lý liên hệ và quan hệ (CRM)",
                "Công cụ và trợ lý AI",
                "Tạo hợp đồng và chữ ký điện tử",
                "Phân tích, báo cáo, và tích hợp",
                "Các ứng dụng bổ sung tùy chọn (\"Banana Apps\")",
              ]} />
              <P>Một số tính năng chỉ có sẵn theo các gói đăng ký trả phí.</P>
            </>
          ),
        },
        {
          id: "accounts",
          title: "Tài khoản & Điều kiện",
          body: (
            <>
              <P>Bạn phải cung cấp thông tin chính xác, đầy đủ và cập nhật khi tạo tài khoản. Bạn chịu trách nhiệm bảo mật thông tin đăng nhập và mọi hoạt động dưới tài khoản của mình.</P>
              <P>Bạn không được:</P>
              <UL items={[
                "Mạo danh người khác hoặc tổ chức khác",
                "Sử dụng nền tảng cho mục đích gian lận hoặc bất hợp pháp",
                "Cố gắng vượt qua các cơ chế kiểm soát hoặc quy kết của hệ thống",
              ]} />
              <P>Banana Cards có quyền tạm ngừng hoặc chấm dứt các tài khoản vi phạm Điều khoản này.</P>
            </>
          ),
        },
        {
          id: "products",
          title: "Sản phẩm, Gói dịch vụ & Giá cả",
          body: (
            <>
              <H3>Phần cứng (Thẻ NFC)</H3>
              <P>Thẻ vật lý được bán riêng hoặc theo gói tùy thuộc vào gói bạn chọn. Sau khi sản xuất hoặc giao hàng, các đơn hàng phần cứng không được hoàn tiền trừ khi luật pháp yêu cầu hoặc thuộc bảo hành cụ thể.</P>
              <H3>Gói Phần mềm</H3>
              <P>Banana Cards cung cấp nhiều gói đăng ký. Tính năng và giới hạn khác nhau theo gói.</P>
              <H3>Thay đổi Giá</H3>
              <P>Chúng tôi có thể cập nhật giá, tính năng, hoặc cấu trúc gói theo thời gian. Các thay đổi áp dụng trong tương lai và không hồi tố các kỳ đã thanh toán trừ khi luật pháp yêu cầu.</P>
            </>
          ),
        },
        {
          id: "trials",
          title: "Dùng thử Miễn phí",
          body: (
            <>
              <P>Banana Cards có thể cung cấp dùng thử miễn phí chỉ dành cho các gói đăng ký phần mềm.</P>
              <UL items={[
                "Dùng thử cung cấp quyền truy cập tạm thời vào các tính năng trả phí được chọn",
                "Dùng thử không đảm bảo phần cứng miễn phí hoặc giảm giá trừ khi được nêu rõ",
                "Dùng thử tự động chuyển sang đăng ký trả phí nếu không hủy trước khi kết thúc",
                "Không hoàn tiền cho các khoản phí phát sinh sau khi dùng thử được chuyển đổi",
              ]} />
              <P>Nếu dùng thử không được chuyển sang gói trả phí, quyền truy cập vào các tính năng trả phí có thể bị giới hạn hoặc vô hiệu hóa.</P>
            </>
          ),
        },
        {
          id: "billing",
          title: "Đăng ký, Thanh toán & Gia hạn",
          body: (
            <UL items={[
              "Đăng ký trả phí được tính phí định kỳ (hàng tháng hoặc hàng năm tùy gói)",
              "Đăng ký tự động gia hạn trừ khi bị hủy trước ngày gia hạn",
              "Bạn chịu trách nhiệm duy trì thông tin thanh toán hợp lệ",
              "Phí không bao gồm thuế áp dụng trừ khi có nêu khác",
              "Việc hủy sẽ dừng thanh toán trong tương lai nhưng không hoàn lại thời gian chưa sử dụng trừ khi luật pháp yêu cầu",
            ]} />
          ),
        },
        {
          id: "data",
          title: "Sở hữu Dữ liệu & Sử dụng Hợp lý",
          body: (
            <>
              <H3>Sở hữu Dữ liệu</H3>
              <P>Bạn giữ quyền sở hữu nội dung, liên hệ và dữ liệu được tải lên nền tảng. Banana Cards xử lý dữ liệu chỉ để cung cấp Dịch vụ.</P>
              <H3>Lưu trữ Dữ liệu</H3>
              <P>Sau khi hủy hoặc chấm dứt, chúng tôi có thể lưu trữ dữ liệu của bạn trong một khoảng thời gian giới hạn trước khi xóa, tùy thuộc vào yêu cầu pháp lý và vận hành.</P>
              <H3>Sử dụng Hợp lý</H3>
              <UL items={[
                "Nhắn tin tự động phải tuân thủ luật chống spam",
                "Sử dụng quá mức hoặc lạm dụng có thể yêu cầu nâng cấp gói hoặc dẫn đến tạm ngừng",
                "Nghiêm cấm cào dữ liệu, thao túng hoặc lạm dụng dữ liệu",
              ]} />
            </>
          ),
        },
        {
          id: "hardware",
          title: "Phần cứng, Vận chuyển & Rủi ro",
          body: (
            <UL items={[
              "Sản xuất bắt đầu khi đơn hàng được xác nhận",
              "Thẻ tùy chỉnh hoặc cá nhân hóa không được trả lại sau khi sản xuất",
              "Rủi ro chuyển giao cho khách hàng khi giao cho đơn vị vận chuyển",
              "Khách hàng chịu trách nhiệm về thuế hải quan, thuế nhập khẩu hoặc phí thông quan",
              "Nếu giao hàng thất bại do sai địa chỉ, có thể áp dụng phí gửi lại",
            ]} />
          ),
        },
        {
          id: "ip",
          title: "Sở hữu Trí tuệ",
          body: (
            <>
              <P>Toàn bộ phần mềm, thương hiệu, thiết kế và hệ thống của Banana Cards thuộc sở hữu của Six Studio Creative FZCO và được cấp phép cho Banana Cards Company Ltd. Bạn không được sao chép, sửa đổi, bán lại hoặc dịch ngược bất kỳ phần nào của Dịch vụ mà không có sự cho phép bằng văn bản.</P>
              <P>Nội dung do người dùng tạo vẫn thuộc sở hữu của bạn nhưng cấp cho Banana Cards một giấy phép giới hạn để vận hành và hiển thị nội dung đó trong Dịch vụ.</P>
            </>
          ),
        },
        {
          id: "termination",
          title: "Tạm ngừng & Chấm dứt Tài khoản",
          body: (
            <>
              <P>Chúng tôi có thể tạm ngừng hoặc chấm dứt quyền truy cập nếu:</P>
              <UL items={[
                "Các Điều khoản này bị vi phạm",
                "Phát hiện hoạt động gian lận, lạm dụng hoặc phi đạo đức",
                "Yêu cầu pháp lý hoặc quy định đòi hỏi",
              ]} />
              <P>Việc chấm dứt không giải phóng bạn khỏi các nghĩa vụ thanh toán đã phát sinh.</P>
            </>
          ),
        },
        {
          id: "warranties",
          title: "Từ chối Bảo đảm",
          body: <P>Dịch vụ được cung cấp theo dạng "nguyên trạng" và "khi có sẵn". Trong phạm vi tối đa được luật pháp cho phép, Banana Cards từ chối mọi bảo đảm, dù rõ ràng hay ngụ ý, bao gồm tính phù hợp cho mục đích cụ thể và tính khả dụng không bị gián đoạn.</P>,
        },
        {
          id: "liability",
          title: "Giới hạn Trách nhiệm",
          body: (
            <>
              <P>Trong phạm vi tối đa được luật pháp cho phép:</P>
              <UL items={[
                "Banana Cards không chịu trách nhiệm cho các thiệt hại gián tiếp, ngẫu nhiên hoặc do hậu quả",
                "Tổng trách nhiệm giới hạn ở số tiền bạn đã thanh toán cho sản phẩm hoặc đăng ký cụ thể gây ra khiếu nại",
              ]} />
            </>
          ),
        },
        {
          id: "law",
          title: "Luật Áp dụng & Liên hệ",
          body: (
            <>
              <P>Các Điều khoản này được điều chỉnh và giải thích theo luật của Các Tiểu vương quốc Ả Rập Thống nhất, trừ khi luật địa phương bắt buộc yêu cầu khác.</P>
              <P>Đối với hỗ trợ, thông báo pháp lý hoặc câu hỏi về các Điều khoản này, liên hệ <a href="mailto:hello@banana.cards" className="font-bold underline-offset-4 hover:underline">hello@banana.cards</a>.</P>
            </>
          ),
        },
      ]}
    />
  );
}